import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { countryCodes } from '../utils/countryCodes';

const API_URL = process.env.REACT_APP_API_URL;

function CheckoutPage({ isMobile, cart, removeFromCart, updateQuantity, getCartTotal, currentPage, setCurrentPage, clearCart, user }) {
  const [customerInfo, setCustomerInfo] = useState({
    name: user?.name || '',
    phone: '',
    countryCode: '+263',
    email: user?.email || '',
    address: '',
    city: 'Chitungwiza',
    deliveryInstructions: '',
    paymentMethod: 'cash'
  });

  const [deliveryOption, setDeliveryOption] = useState('delivery');
  const [deliveryFee, setDeliveryFee] = useState(5.00);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setCustomerInfo(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeliveryOptionChange = (option) => {
    setDeliveryOption(option);
    if (option === 'pickup') {
      setDeliveryFee(0);
    } else {
      setDeliveryFee(5.00);
    }
  };

  const calculateDeliveryFee = () => {
    if (deliveryOption === 'pickup') {
      return 0;
    }
    
    switch (customerInfo.city.toLowerCase()) {
      case 'harare':
        return 10.00;
      case 'chitungwiza':
        return 5.00;
      default:
        return 15.00;
    }
  };

  useEffect(() => {
    setDeliveryFee(calculateDeliveryFee());
  }, [deliveryOption, customerInfo.city]);

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      alert('Your cart is empty!');
      return;
    }

    setIsSubmitting(true);

    const orderData = {
      customerInfo: {
        ...customerInfo,
        fullPhone: `${customerInfo.countryCode} ${customerInfo.phone}`,
        userId: user?.id || null
      },
      cart,
      subtotal: getCartTotal(),
      deliveryFee,
      total: getCartTotal() + deliveryFee,
      deliveryOption,
      paymentMethod: customerInfo.paymentMethod
    };

    try {
      const response = await axios.post(
        `${API_URL}/index.php`,
        {
          apiType: "submitOrder",
          ...orderData
        }
      );

      if (response.data.success) {
        setOrderSubmitted(true);
        clearCart();
        alert('Order submitted successfully!');
      } else {
        alert(response.data.message || 'Failed to submit order. Please try again.');
      }
    } catch (error) {
      console.error("Error posting order:", error);
      alert("Error submitting order. Please check your internet connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header 
          isMobile={isMobile} 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          cartItemCount={0}
          user={user}
        />
        
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-6xl text-green-500 mb-6">✓</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Order Confirmed!</h2>
            <p className="text-gray-600 mb-4">
              Thank you for your order, {customerInfo.name}! Your order has been received and will be processed shortly.
            </p>
            <p className="text-gray-600 mb-8">
              We'll contact you at {customerInfo.countryCode} {customerInfo.phone} to confirm {deliveryOption === 'pickup' ? 'pickup' : 'delivery'} details.
            </p>
            <button 
              className="px-8 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-semibold"
              onClick={() => setCurrentPage('shop')}
            >
              Continue Shopping
            </button>
          </div>
        </div>

        <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        isMobile={isMobile} 
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        cartItemCount={cart.reduce((total, item) => total + (item.purchaseType === 'amount' ? 1 : item.quantity), 0)}
        user={user}
      />
      
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">Checkout</h1>
        
        {!user && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-center">
            <p className="text-gray-700">
              Have an account? 
              <button 
                className="text-yellow-600 hover:text-yellow-700 font-semibold mx-1"
                onClick={() => setCurrentPage('login')}
              >
                Sign in
              </button>
              for a faster checkout experience!
            </p>
          </div>
        )}
        
        <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-8`}>
          <div className={`${isMobile ? 'w-full' : 'w-2/3'}`}>
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
              
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 text-lg mb-4">Your cart is empty</p>
                  <button 
                    className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg"
                    onClick={() => setCurrentPage('shop')}
                  >
                    Start Shopping
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-8">
                    {cart.map((item, index) => (
                      <div key={`${item.id}-${index}`} className="flex items-center justify-between border-b pb-4">
                        <div className="flex items-center space-x-4">
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="w-16 h-16 rounded object-cover"
                          />
                          <div>
                            <h4 className="font-semibold text-gray-900">{item.name}</h4>
                            <p className="text-yellow-600 font-semibold">
                              {item.purchaseType === 'amount' ? (
                                <>${parseFloat(item.customAmount).toFixed(2)}</>
                              ) : (
                                <>${item.price.toFixed(2)}/kg</>
                              )}
                            </p>
                            {item.purchaseType === 'amount' && (
                              <p className="text-sm text-gray-500">
                                Weight: {item.calculatedWeight} kg
                              </p>
                            )}
                            {item.purchaseType === 'weight' && item.quantity !== 1 && (
                              <p className="text-sm text-gray-500">
                                Weight: {item.quantity} kg
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-6">
                          {item.purchaseType !== 'amount' && (
                            <div className="flex items-center space-x-3">
                              <button 
                                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded"
                                onClick={() => {
                                  if (item.purchaseType === 'customWeight') {
                                    updateQuantity(item.id, Math.max(0.1, item.quantity - 0.1));
                                  } else {
                                    updateQuantity(item.id, item.quantity - 1);
                                  }
                                }}
                              >
                                -
                              </button>
                              <span className="w-12 text-center font-medium">
                                {item.purchaseType === 'customWeight' ? item.quantity.toFixed(1) : item.quantity}
                                {item.purchaseType === 'customWeight' && 'kg'}
                              </span>
                              <button 
                                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded"
                                onClick={() => {
                                  if (item.purchaseType === 'customWeight') {
                                    updateQuantity(item.id, item.quantity + 0.1);
                                  } else {
                                    updateQuantity(item.id, item.quantity + 1);
                                  }
                                }}
                              >
                                +
                              </button>
                            </div>
                          )}
                          <div className="text-right">
                            <div className="font-bold text-gray-900">
                              {item.purchaseType === 'amount' ? (
                                <>${parseFloat(item.customAmount).toFixed(2)}</>
                              ) : (
                                <>${(item.price * item.quantity).toFixed(2)}</>
                              )}
                            </div>
                          </div>
                          <button 
                            className="text-red-500 hover:text-red-700 text-xl"
                            onClick={() => removeFromCart(item.id)}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span className="font-semibold">${getCartTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Delivery:</span>
                      <span className="font-semibold">${deliveryFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-3">
                      <span className="text-xl font-bold">Total:</span>
                      <span className="text-xl font-bold">
                        ${(getCartTotal() + deliveryFee).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {cart.length > 0 && (
            <div className={`${isMobile ? 'w-full' : 'w-1/3'}`}>
              <div className="bg-white rounded-xl shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Information</h2>
                
                <form onSubmit={handleSubmitOrder} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={customerInfo.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <div className="flex gap-2">
                      <select 
                        name="countryCode"
                        value={customerInfo.countryCode}
                        onChange={handleInputChange}
                        className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        name="phone"
                        value={customerInfo.phone}
                        onChange={handleInputChange}
                        required
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        placeholder="Phone number"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={customerInfo.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Option</label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="delivery"
                          checked={deliveryOption === 'delivery'}
                          onChange={() => handleDeliveryOptionChange('delivery')}
                          className="mr-2"
                        />
                        <span>Delivery (${deliveryFee.toFixed(2)})</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="pickup"
                          checked={deliveryOption === 'pickup'}
                          onChange={() => handleDeliveryOptionChange('pickup')}
                          className="mr-2"
                        />
                        <span>Pickup (Free) - Zvido Business Centre</span>
                      </label>
                    </div>
                  </div>
                  
                  {deliveryOption === 'delivery' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Address *</label>
                        <textarea
                          name="address"
                          value={customerInfo.address}
                          onChange={handleInputChange}
                          required
                          rows="3"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          placeholder="Full street address, house number, etc."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">City/Area *</label>
                        <input
                          type="text"
                          name="city"
                          value={customerInfo.city}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          placeholder="e.g., Chitungwiza, Harare, etc."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Instructions</label>
                        <textarea
                          name="deliveryInstructions"
                          value={customerInfo.deliveryInstructions}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          placeholder="Any specific delivery instructions, landmarks, etc."
                        />
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                    <select
                      name="paymentMethod"
                      value={customerInfo.paymentMethod}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    >
                      <option value="cash">Cash on Delivery</option>
                      <option value="mobile">Mobile Money</option>
                      <option value="card">Debit/Credit Card</option>
                      <option value="transfer">Bank Transfer</option>
                    </select>
                  </div>

                  {customerInfo.paymentMethod === 'transfer' && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Bank Transfer Details</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Bank:</strong> Your Bank Name<br/>
                        <strong>Account Name:</strong> Sir Henry's Butchery<br/>
                        <strong>Account Number:</strong> 1234567890<br/>
                        <strong>Branch Code:</strong> 12345
                      </p>
                      <p className="text-xs text-gray-500">
                        Please use your order number as reference when transferring.
                      </p>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    className={`w-full bg-yellow-600 hover:bg-yellow-700 text-white py-4 rounded-lg font-bold text-lg ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting || cart.length === 0}
                  >
                    {isSubmitting ? 'Submitting Order...' : `Place Order : $${(getCartTotal() + deliveryFee).toFixed(2)}`}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
    </div>
  );
}

export default CheckoutPage;